import { useAssessmentScoreLabels } from '@/api/assessments.hook'
import { AssessmentScore } from '@/gen/assessment/v1/assessment_score_pb'

import { ASSESSED_SCORES } from '@/lib/connect/proto-types'

import { Badge, BadgeProps } from '@/components/lemmala-design-system/badge/badge'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'

const assessmentScoreToVariant: Record<AssessmentScore, BadgeProps['variant']> = {
  [AssessmentScore.ACCEPTABLE]: 'neutral',
  [AssessmentScore.CONCERNING]: 'warning',
  [AssessmentScore.UNACCEPTABLE]: 'danger',
  [AssessmentScore.NOT_ASSESSED]: 'neutral',
  [AssessmentScore.UNSPECIFIED]: undefined,
}

const assessmentScoreToBadgeStyle: Record<AssessmentScore, BadgeProps['badgeStyle']> = {
  [AssessmentScore.ACCEPTABLE]: 'subtle',
  [AssessmentScore.CONCERNING]: 'subtle',
  [AssessmentScore.UNACCEPTABLE]: 'subtle',
  [AssessmentScore.NOT_ASSESSED]: 'ghost',
  [AssessmentScore.UNSPECIFIED]: undefined,
}

type AssessmentScoreBadgeProps = {
  assessmentScore: AssessmentScore
  size?: BadgeProps['size']
  showTooltip?: boolean
  label?: string
}

export const AssessmentScoreBadge = ({
  assessmentScore,
  size,
  showTooltip = false,
  label,
}: AssessmentScoreBadgeProps) => {
  const { scoreLabels } = useAssessmentScoreLabels()
  const variant = assessmentScoreToVariant[assessmentScore]
  if (!variant) return null

  return (
    <Tooltip
      tooltipDisabled={!showTooltip}
      trigger={
        <BaseAssessmentScoreBadge
          assessmentScore={assessmentScore}
          size={size}
          label={label ?? scoreLabels[assessmentScore]}
        />
      }
      size='large'
    >
      <div className='text-main-foreground-secondary text-body-small'>
        Reflects how well validated controls reduce the third-party’s risk exposure.
      </div>
      <div className='mt-2 flex items-center gap-2'>
        {ASSESSED_SCORES.map((currentAssessmentScore) => (
          <div key={currentAssessmentScore}>
            {currentAssessmentScore === assessmentScore ? (
              <BaseAssessmentScoreBadge
                assessmentScore={currentAssessmentScore}
                label={scoreLabels[currentAssessmentScore]}
              />
            ) : (
              <Badge variant={'neutral'} badgeStyle={'ghost'}>
                {scoreLabels[currentAssessmentScore]}
              </Badge>
            )}
          </div>
        ))}
      </div>
    </Tooltip>
  )
}

const BaseAssessmentScoreBadge = ({ assessmentScore, size, label }: AssessmentScoreBadgeProps) => {
  const variant = assessmentScoreToVariant[assessmentScore]
  if (!variant) return null

  return (
    <Badge variant={variant} badgeStyle={assessmentScoreToBadgeStyle[assessmentScore]} size={size}>
      {label}
    </Badge>
  )
}
